import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Disc3, Send, CheckCircle2, Music2, TrendingUp, Users, Headphones, Zap, Clock, MessageSquare, Star, Crown, Check, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { Header } from "../components/Header";
import { toast } from "sonner";
import axios from "axios";

// Use current origin for API calls so it works on both preview and custom domain
const API = `${window.location.origin}/api`;

// Animation variants
const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: "easeOut" }
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Navigation - uses shared Header component

// Hero Section
const HeroSection = () => {
  const scrollToForm = () => {
    document.getElementById('submit-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1633319987325-aae32f3d8dd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxkYXJrJTIwY29uY3JldGUlMjB0ZXh0dXJlJTIwd2FsbHxlbnwwfHx8fDE3NzE0MTI0MDV8MA&ixlib=rb-4.1.0&q=85')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
      data-testid="hero-section">

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/85" />
      
      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-left">
        <motion.h1
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.1 }}
          className="font-heading text-6xl md:text-8xl font-bold uppercase tracking-tighter leading-none text-white mb-8">

          For Records That<br />
          <span className="text-neon">Move Rooms.</span>
        </motion.h1>
        
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.2 }}
          className="max-w-2xl mb-12">

          <p className="text-lg md:text-xl text-zinc-400 leading-relaxed mb-6">
            Test Press Club scouts underground dance records with real momentum and circulates them to our private industry network.
          </p>
          <p className="text-zinc-500 text-base">
            We work selectively.<br />
            No submission fees. Commission only if a deal closes.
          </p>
        </motion.div>
        
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.3 }}>

          <Button
            onClick={scrollToForm}
            className="bg-neon text-black hover:bg-white hover:text-black rounded-none uppercase font-bold tracking-wider px-10 py-6 text-lg transition-colors duration-300 border border-neon hover:border-white"
            data-testid="hero-cta-btn">

            Submit Your Track
          </Button>
        </motion.div>
      </div>
      
      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}>

        <div className="w-px h-16 bg-zinc-700" />
      </motion.div>
    </section>);

};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
  {
    number: "01",
    title: "Submit Your Record",
    description: "Share your track and current traction."
  },
  {
    number: "02",
    title: "We Review & Select",
    description: "We assess momentum, club potential, and market fit."
  },
  {
    number: "03",
    title: "We Circulate",
    description: "If selected, your record is shared within our private network of industry decision-makers."
  }];


  return (
    <section className="py-24 md:py-32 bg-[#09090b] border-t border-zinc-800" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div {...fadeUp}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">Process</p>
          <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-16">
            How It Works
          </h2>
        </motion.div>
        
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}>

          {steps.map((step, index) =>
          <motion.div
            key={step.number}
            variants={fadeUp}
            className="group">

              <div className="border border-zinc-800 p-8 h-full hover:border-neon/50 transition-colors duration-300">
                <span className="text-6xl font-heading font-bold text-zinc-800 group-hover:text-neon/30 transition-colors duration-300">
                  {step.number}
                </span>
                <h3 className="font-heading text-2xl md:text-3xl font-medium uppercase tracking-wide text-white mt-4 mb-4">
                  {step.title}
                </h3>
                <p className="text-zinc-500 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>);

};

// What We Look For Section
const WhatWeLookForSection = () => {
  const criteria = [
  { icon: Music2, text: "Underground dance/club tracks" },
  { icon: TrendingUp, text: "Strong momentum or traction" },
  { icon: Users, text: "Independent / unsigned artists preferred" },
  { icon: Headphones, text: "Club-ready or sync-ready production" }];


  return (
    <section
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1672938662121-9182d3060dd5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHx2aW55bCUyMHJlY29yZCUyMHR1cm50YWJsZSUyMGNsb3NlJTIwdXAlMjBkYXJrfGVufDB8fHx8MTc3MTQxMjQwNnww&ixlib=rb-4.1.0&q=85')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
      data-testid="what-we-look-for-section">

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/90" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <motion.div {...fadeUp}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">Criteria</p>
          <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-16">
            What We Look For
          </h2>
        </motion.div>
        
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}>

          {criteria.map((item, index) =>
          <motion.div
            key={index}
            variants={fadeUp}
            className="flex items-center gap-6 p-6 border border-zinc-800 hover:border-neon/50 transition-colors duration-300 group">

              <item.icon className="w-8 h-8 text-neon flex-shrink-0" />
              <span className="text-lg text-zinc-300 group-hover:text-white transition-colors duration-300">
                {item.text}
              </span>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>);

};

// Pro Access Section
const ProAccessSection = () => {
  const benefits = [
  { icon: Disc3, text: "Submit up to 5 tracks per month" },
  { icon: Clock, text: "Priority review within 48–72 hours" },
  { icon: MessageSquare, text: "Curated feedback on your track's momentum" },
  { icon: Star, text: "Early visibility to our private industry network" },
  { icon: Zap, text: "Lower commission on successful placement (10% vs 15%)" }];


  const scrollToForm = () => {
    document.getElementById('submit-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="pro-access"
      className="py-24 md:py-32 bg-[#09090b] border-t border-zinc-800 relative overflow-hidden"
      data-testid="pro-access-section">

      {/* Subtle accent glow */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-neon/5 blur-[150px] rounded-full" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left - Content */}
          <div>
            <motion.div {...fadeUp}>
              <div className="flex items-center gap-3 mb-6">
                <Crown className="w-5 h-5 text-neon" />
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-neon">Pro Access</p>
              </div>
              <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-6">
                Priority Access<br />for Artists
              </h2>
              <p className="text-xl text-zinc-400 leading-relaxed mb-8">
                Unlock faster review, more submissions, and curated insights for your tracks.
              </p>
            </motion.div>
            
            <motion.div
              className="space-y-4 mb-10"
              variants={staggerContainer}
              initial="initial"
              whileInView="whileInView"
              viewport={{ once: true }}>

              {benefits.map((item, index) =>
              <motion.div
                key={index}
                variants={fadeUp}
                className="flex items-center gap-4 group">

                  <div className="w-10 h-10 border border-zinc-800 flex items-center justify-center group-hover:border-neon/50 transition-colors duration-300">
                    <item.icon className="w-5 h-5 text-neon" />
                  </div>
                  <span className="text-zinc-300 group-hover:text-white transition-colors duration-300">
                    {item.text}
                  </span>
                </motion.div>
              )}
            </motion.div>

            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.3 }}>
              <Link to="/pro">
                <Button
                  className="bg-neon text-black hover:bg-white hover:text-black rounded-none uppercase font-bold tracking-wider px-10 py-6 text-lg transition-colors duration-300 border border-neon hover:border-white"
                  data-testid="pro-access-cta-btn">
                  Upgrade to Pro Access — £14.99/mo
                </Button>
              </Link>
              <p className="text-zinc-600 text-sm mt-4">
                Optional. Free submissions still welcome.
              </p>
            </motion.div>
          </div>

          {/* Right - Visual/Card */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="lg:mt-12">

            <div className="border border-zinc-800 bg-zinc-900/30 p-8 backdrop-blur-sm">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-6">Pro Access is for</p>
              <p className="text-zinc-400 leading-relaxed mb-6">
                Artists who want more insight, faster review, and higher priority in our circulation network. 
                We keep our network tight and our standards high — Pro Access gives you an edge, not a guarantee.
              </p>
              <div className="border-t border-zinc-800 pt-6">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">Free tier remains</p>
                <p className="text-zinc-500 text-sm">
                  1 submission per month, standard review timeline. 
                  Commission: 15% on successful placements.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>);

};

// How Pro Access Works Section
const HowProAccessWorksSection = () => {
  const steps = [
  {
    number: "01",
    title: "Upgrade Your Account",
    description: "Submit more tracks, get faster review. Your submissions move to the front of our queue."
  },
  {
    number: "02",
    title: "Get Curated Feedback",
    description: "See how your track measures up and where it fits in the club landscape."
  },
  {
    number: "03",
    title: "Priority Circulation",
    description: "Selected tracks reach our private network before standard submissions."
  }];


  return (
    <section className="py-24 md:py-32 bg-[#0d0d0f] border-t border-zinc-800" data-testid="how-pro-works-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div {...fadeUp}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">Pro Process</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-16">
            How Pro Access Enhances<br />Your Submission
          </h2>
        </motion.div>
        
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}>

          {steps.map((step, index) =>
          <motion.div
            key={step.number}
            variants={fadeUp}
            className="group relative">

              <div className="border-l-2 border-neon/30 pl-6 py-2 group-hover:border-neon transition-colors duration-300">
                <span className="text-4xl font-heading font-bold text-neon/30 group-hover:text-neon/50 transition-colors duration-300">
                  {step.number}
                </span>
                <h3 className="font-heading text-xl md:text-2xl font-medium uppercase tracking-wide text-white mt-2 mb-3">
                  {step.title}
                </h3>
                <p className="text-zinc-500 leading-relaxed text-sm">
                  {step.description}
                </p>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Commission note */}
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.4 }}
          className="mt-16 border border-zinc-800 p-6 max-w-2xl">

          <p className="text-zinc-500 text-sm">
            <span className="text-zinc-400 font-bold">Commission rates:</span> Free = 15% / Pro = 10% on successful placements. 
            No upfront fees. We only earn when you do.
          </p>
        </motion.div>
      </div>
    </section>);

};

// Free vs Pro Comparison Section
const ComparisonSection = () => {
  const features = [
  { feature: "Tracks per month", free: "1", pro: "Up to 5" },
  { feature: "Review speed", free: "Standard", pro: "Priority (48–72 hrs)" },
  { feature: "Feedback", free: "Minimal", pro: "Curated insights" },
  { feature: "Circulation", free: "Standard queue", pro: "Priority to network" },
  { feature: "Commission on deals", free: "15%", pro: "10%" }];


  return (
    <section
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1665623328945-35acef28302a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHx1bmRlcmdyb3VuZCUyMHRlY2hubyUyMGNsdWIlMjBjcm93ZCUyMGRhcmslMjBzaWxob3VldHRlfGVufDB8fHx8MTc3MTQxMjQxOXww&ixlib=rb-4.1.0&q=85')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
      data-testid="comparison-section">

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/95" />
      
      <div className="relative z-10 max-w-4xl mx-auto px-6">
        <motion.div {...fadeUp} className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">Compare</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white">
            Free vs Pro
          </h2>
        </motion.div>
        
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.1 }}
          className="border border-zinc-800 overflow-hidden">

          {/* Header */}
          <div className="grid grid-cols-3 border-b border-zinc-800 bg-zinc-900/50">
            <div className="p-4 md:p-6">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Feature</span>
            </div>
            <div className="p-4 md:p-6 border-l border-zinc-800 text-center">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Free</span>
            </div>
            <div className="p-4 md:p-6 border-l border-zinc-800 text-center bg-neon/5">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-neon">Pro Access</span>
            </div>
          </div>
          
          {/* Rows */}
          {features.map((row, index) =>
          <div
            key={index}
            className="grid grid-cols-3 border-b border-zinc-800 last:border-b-0 hover:bg-zinc-900/30 transition-colors duration-200">

              <div className="p-4 md:p-6 flex items-center">
                <span className="text-zinc-300 text-sm">{row.feature}</span>
              </div>
              <div className="p-4 md:p-6 border-l border-zinc-800 flex items-center justify-center">
                <span className="text-zinc-500 text-sm">{row.free}</span>
              </div>
              <div className="p-4 md:p-6 border-l border-zinc-800 flex items-center justify-center bg-neon/5">
                <span className="text-white text-sm font-medium">{row.pro}</span>
              </div>
            </div>
          )}
        </motion.div>

        <motion.p
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.2 }}
          className="text-zinc-600 text-sm text-center mt-6">

          Pro Access is optional. Free submissions remain fully welcome.
        </motion.p>
      </div>
    </section>);

};

// Submission Form Section
const SubmissionFormSection = () => {
  const formRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [freeTierLimitReached, setFreeTierLimitReached] = useState(false);
  const [formData, setFormData] = useState({
    artist_name: "",
    email: "",
    instagram_profile: "",
    track_title: "",
    spotify_soundcloud_link: "",
    tiktok_link: "",
    monthly_listeners: "",
    traction_note: "",
    rights_confirmed: false,
    terms_confirmed: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.rights_confirmed) {
      toast.error("Please confirm you control the rights to this recording");
      return;
    }

    if (!formData.terms_confirmed) {
      toast.error("Please agree to the Terms & Conditions");
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        ...formData,
        monthly_listeners: formData.monthly_listeners ? parseInt(formData.monthly_listeners) : null
      };

      await axios.post(`${API}/submissions`, submitData);
      setIsSubmitted(true);
      toast.success("Submission received successfully!");
    } catch (error) {
      console.error("Submission error:", error);
      if (error.response?.data?.detail === "FREE_TIER_LIMIT_REACHED") {
        setFreeTierLimitReached(true);
      } else {
        toast.error(error.response?.data?.detail || "Failed to submit. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Free tier limit reached view
  if (freeTierLimitReached) {
    return (
      <section id="submit-form" className="py-24 md:py-32 bg-[#09090b] border-t border-zinc-800" data-testid="submission-form-section">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <Crown className="w-16 h-16 text-neon mx-auto mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-6">
              Free Submission Used
            </h2>
            <p className="text-zinc-400 text-lg mb-4">
              You have already used your free submission.
            </p>
            <p className="text-zinc-500 mb-8">
              Upgrade to Pro Access to submit more tracks and receive priority review.
            </p>
            <Link to="/pro">
              <Button className="bg-neon text-black hover:bg-white hover:text-black rounded-none uppercase font-bold tracking-wider px-10 py-5 transition-colors duration-300 border border-neon hover:border-white">
                Upgrade to Pro Access
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    );
  }

  if (isSubmitted) {
    return (
      <section id="submit-form" className="py-24 md:py-32 bg-[#09090b] border-t border-zinc-800" data-testid="submission-form-section">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <CheckCircle2 className="w-16 h-16 text-neon mx-auto mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-6">
              Submission Received
            </h2>
            <p className="text-zinc-400 text-lg">
              Thanks. If your track fits our current briefs, we'll be in touch.
            </p>
          </motion.div>
        </div>
      </section>);

  }

  return (
    <section id="submit-form" className="py-24 md:py-32 bg-[#09090b] border-t border-zinc-800" data-testid="submission-form-section">
      <div className="max-w-2xl mx-auto px-6">
        <motion.div {...fadeUp}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">Application</p>
          <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-4">
            Submit Your Track
          </h2>
          <p className="text-zinc-500 mb-12">All fields marked with * are required.</p>
        </motion.div>
        
        <motion.form
          ref={formRef}
          onSubmit={handleSubmit}
          className="space-y-8"
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.1 }}>

          {/* Artist Name */}
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">
              Artist Name *
            </label>
            <Input
              type="text"
              name="artist_name"
              value={formData.artist_name}
              onChange={handleChange}
              required
              placeholder="Your artist name"
              className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
              data-testid="artist-name-input" />

          </div>

          {/* Email */}
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">
              Email *
            </label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="your@email.com"
              className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
              data-testid="email-input" />

          </div>

          {/* Instagram */}
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">
              Instagram Profile
            </label>
            <Input
              type="text"
              name="instagram_profile"
              value={formData.instagram_profile}
              onChange={handleChange}
              placeholder="@yourusername"
              className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
              data-testid="instagram-input" />

          </div>

          {/* Track Title */}
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">
              Track Title *
            </label>
            <Input
              type="text"
              name="track_title"
              value={formData.track_title}
              onChange={handleChange}
              required
              placeholder="Name of your track"
              className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
              data-testid="track-title-input" />

          </div>

          {/* Spotify/SoundCloud Link */}
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">
              Spotify / SoundCloud Link *
            </label>
            <Input
              type="url"
              name="spotify_soundcloud_link"
              value={formData.spotify_soundcloud_link}
              onChange={handleChange}
              required
              placeholder="https://..."
              className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
              data-testid="spotify-link-input" />

          </div>

          {/* TikTok Link */}
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">
              TikTok Sound Link
            </label>
            <Input
              type="url"
              name="tiktok_link"
              value={formData.tiktok_link}
              onChange={handleChange}
              placeholder="https://tiktok.com/..."
              className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
              data-testid="tiktok-link-input" />

          </div>

          {/* Monthly Listeners */}
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">
              Monthly Spotify Listeners
            </label>
            <Input
              type="number"
              name="monthly_listeners"
              value={formData.monthly_listeners}
              onChange={handleChange}
              placeholder="e.g., 50000"
              className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
              data-testid="monthly-listeners-input" />

          </div>

          {/* Traction Note */}
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">
              Short Note About Traction
            </label>
            <Textarea
              name="traction_note"
              value={formData.traction_note}
              onChange={handleChange}
              placeholder="Tell us about DJ support, playlist placements, viral moments..."
              rows={4}
              className="bg-transparent border-2 border-zinc-800 focus:border-neon rounded-none px-4 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white resize-none"
              data-testid="traction-note-input" />

          </div>

          {/* Rights Checkbox */}
          <div className="flex items-start gap-4 pt-4">
            <Checkbox
              id="rights_confirmed"
              name="rights_confirmed"
              checked={formData.rights_confirmed}
              onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, rights_confirmed: checked }))}
              className="mt-1 border-zinc-600 data-[state=checked]:bg-neon data-[state=checked]:border-neon rounded-none"
              data-testid="rights-checkbox" />

            <label htmlFor="rights_confirmed" className="text-zinc-400 text-sm leading-relaxed cursor-pointer">
              I confirm I control or represent the rights to this recording. *
            </label>
          </div>

          {/* Terms Checkbox */}
          <div className="flex items-start gap-4">
            <Checkbox
              id="terms_confirmed"
              name="terms_confirmed"
              checked={formData.terms_confirmed}
              onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, terms_confirmed: checked }))}
              className="mt-1 border-zinc-600 data-[state=checked]:bg-neon data-[state=checked]:border-neon rounded-none"
              data-testid="terms-checkbox" />

            <label htmlFor="terms_confirmed" className="text-zinc-400 text-sm leading-relaxed cursor-pointer">
              I confirm that I have read and agree to the Test Press Club{" "}
              <Link to="/terms" className="text-neon hover:text-white underline transition-colors duration-300">
                Terms & Conditions
              </Link>. *
            </label>
          </div>

          {/* Submit Button */}
          <div className="pt-8">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-neon text-black hover:bg-white hover:text-black rounded-none uppercase font-bold tracking-wider px-10 py-6 text-lg transition-colors duration-300 border border-neon hover:border-white disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-btn">

              {isSubmitting ?
              <span className="flex items-center justify-center gap-3">
                  <span className="animate-spin">
                    <Disc3 className="w-5 h-5" />
                  </span>
                  Submitting...
                </span> :

              <span className="flex items-center justify-center gap-3">
                  <Send className="w-5 h-5" />
                  Submit for Review
                </span>
              }
            </Button>
          </div>
        </motion.form>
      </div>
    </section>);

};

// Credibility / Industry Section
const CredibilitySection = () => {
  const labels = [
    { name: "ATLANTIC", tooltip: "Track(s) from TPC featured here", logo: "https://customer-assets.emergentagent.com/job_vinyl-scouts/artifacts/9z9u2jtv_atlantic%20logo.png" },
    { name: "POSITIVA", tooltip: "Track(s) from TPC featured here", logo: "https://customer-assets.emergentagent.com/job_vinyl-scouts/artifacts/l2zxqss6_positiva.jpg" },
    { name: "RELENTLESS", tooltip: "Track(s) from TPC featured here", logo: "https://customer-assets.emergentagent.com/job_vinyl-scouts/artifacts/3t1bzaf8_relentless%20logo.png" },
    { name: "NQ", tooltip: "Track(s) from TPC featured here", logo: "https://customer-assets.emergentagent.com/job_vinyl-scouts/artifacts/xt45ke25_NQ%20logo.png" },
    { name: "CREAMFIELDS", tooltip: "Track(s) from TPC featured here", logo: "https://customer-assets.emergentagent.com/job_vinyl-scouts/artifacts/dhff13dq_creamfields%20logo.png" },
    { name: "VIRGIN", tooltip: "Track(s) from TPC featured here", logo: "https://customer-assets.emergentagent.com/job_vinyl-scouts/artifacts/qp7kiyid_virgin%20logo.jpg" },
    { name: "MINISTRY", tooltip: "Track(s) from TPC featured here", logo: "https://customer-assets.emergentagent.com/job_vinyl-scouts/artifacts/rhnk03wj_ministry%20logo.png" },
    { name: "COLUMBIA", tooltip: "Track(s) from TPC featured here", logo: "https://customer-assets.emergentagent.com/job_vinyl-scouts/artifacts/r6q1pab0_columbia%20logo.png" },
    { name: "SONY", tooltip: "Track(s) from TPC featured here", logo: "https://customer-assets.emergentagent.com/job_vinyl-scouts/artifacts/cn5y44ab_sony%20logo.jpg" },
    { name: "UNIVERSAL", tooltip: "Track(s) from TPC featured here", logo: "https://customer-assets.emergentagent.com/job_vinyl-scouts/artifacts/304vyrbk_universal%20logo.png" },
    { name: "WARNER", tooltip: "Track(s) from TPC featured here", logo: "https://customer-assets.emergentagent.com/job_vinyl-scouts/artifacts/pov6q2li_warner%20logo.png" },
    { name: "DEFECTED", tooltip: "Track(s) from TPC featured here", logo: "https://customer-assets.emergentagent.com/job_vinyl-scouts/artifacts/5pids9pt_defected%20logo.png" }
  ];

  const scrollToForm = () => {
    document.getElementById('submit-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      className="py-24 md:py-32 bg-[#09090b] border-t border-zinc-800 relative overflow-hidden"
      data-testid="credibility-section">

      {/* Subtle texture overlay */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1633319987325-aae32f3d8dd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxkYXJrJTIwY29uY3JldGUlMjB0ZXh0dXJlJTIwd2FsbHxlbnwwfHx8fDE3NzE0MTI0MDV8MA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }} />

      
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div {...fadeUp} className="text-center mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">Industry Network</p>
          <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-6">
            Trusted by the<br /><span className="text-neon">Underground</span>
          </h2>
          <p className="text-zinc-400 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            TPC circulates underground club records to respected labels, DJs, and industry buyers. 
            Our network spans top players in the UK, EU, and US dance scene.
          </p>
        </motion.div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-16">
          {/* Left Column - Pitching */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}>

            <div className="border-l-2 border-neon/30 pl-6">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">Pitching</p>
              <p className="text-zinc-300 text-lg leading-relaxed">
                We pitch selected tracks to respected labels and key industry partners, 
                ensuring every record reaches the right ears. Our curated approach means 
                quality over quantity — no spam, no mass emails, just targeted placements 
                to decision-makers who matter.
              </p>
            </div>
          </motion.div>

          {/* Right Column - Placements */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}>

            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-6">
              Record labels we've placed with
            </p>
            
            {/* Logo Grid */}
            <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
              {labels.map((label, index) =>
              <motion.div
                key={label.name}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="group relative">

                  <div className="border border-zinc-800 bg-zinc-900/50 p-3 h-20 flex items-center justify-center hover:border-neon/50 hover:bg-zinc-800/50 transition-all duration-300 cursor-pointer group-hover:scale-105">
                    {label.logo ? (
                      <img 
                        src={label.logo} 
                        alt={label.name}
                        className="max-h-12 max-w-full object-contain grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300"
                      />
                    ) : (
                      <span className="font-heading text-sm md:text-base font-bold tracking-wider text-zinc-600 group-hover:text-zinc-300 transition-colors duration-300">
                        {label.name}
                      </span>
                    )}
                  </div>
                  {/* Tooltip */}
                  <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-zinc-900 border border-zinc-700 px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap z-20">
                    <span className="text-xs text-zinc-400">{label.tooltip}</span>
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full border-4 border-transparent border-t-zinc-700" />
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.3 }}
          className="text-center">

          <p className="text-zinc-500 text-sm mb-6">Want your track considered?</p>
          <Button
            onClick={scrollToForm}
            className="bg-transparent text-neon border border-neon hover:bg-neon hover:text-black rounded-none uppercase font-bold tracking-wider px-10 py-5 text-base transition-colors duration-300"
            data-testid="credibility-cta-btn">

            Submit Your Track
          </Button>
        </motion.div>
      </div>
    </section>);

};

// Footer Component
const Footer = () => {
  return (
    <footer className="py-12 bg-[#09090b] border-t border-zinc-800" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Disc3 className="w-5 h-5 text-neon" />
            <div className="bg-white/95 px-2 py-1 rounded-sm">
              <img 
                src="https://customer-assets.emergentagent.com/job_vinyl-scouts/artifacts/hae9l8sx_TESTPLOGOSTR.png" 
                alt="Test Press Club" 
                className="h-5 w-auto"
              />
            </div>
            <span className="text-zinc-600 text-sm">
              © 2026
            </span>
          </div>
          <div className="flex items-center gap-4 text-zinc-600 text-sm">
            <Link to="/dj-list" className="hover:text-neon transition-colors duration-300">
              Join the DJ List
            </Link>
            <span className="text-zinc-700">|</span>
            <Link to="/terms" className="hover:text-neon transition-colors duration-300">
              Terms & Conditions
            </Link>
            <span className="text-zinc-700">|</span>
            <span>Private Music Deal Placement</span>
          </div>
        </div>
      </div>
    </footer>);

};

// Main Landing Page Component
export default function LandingPage() {
  const scrollToForm = () => {
    document.getElementById('submit-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-[#09090b] min-h-screen">
      {/* Noise overlay */}
      <div className="noise-overlay" />
      
      <Header onSubmitClick={scrollToForm} />
      <HeroSection />
      <HowItWorksSection />
      <WhatWeLookForSection />
      <CredibilitySection />
      <ProAccessSection />
      <HowProAccessWorksSection />
      <ComparisonSection />
      <SubmissionFormSection />
      <Footer />
    </div>);

}